<script setup lang="ts">
import { ref } from 'vue'
import type { IDamageInstance } from '@/types/rc/shipments'
import { ElMessage } from 'element-plus'
import { downloadDamagesPDF } from '@/services/rc.service'

const open = defineModel<boolean>({ default: false })
const props = defineProps<{ containerId: number; containerNumber: string; damages: IDamageInstance[] }>()
const disableDownloadBtn = ref(false)

const openDamagesPDF = async () => {
  disableDownloadBtn.value = true

  let newTab
  try {
    newTab = window.open()
    if (!newTab) {
      ElMessage({
        type: 'error',
        message: 'Failed to open a new tab, check your browser settings.',
      })
      return
    }

    newTab.document.write('Preparing Damages PDF...')
    const data = await downloadDamagesPDF(props.containerId)
    const fileUrl = window.URL.createObjectURL(data)
    newTab.location.assign(fileUrl)
    URL.revokeObjectURL(fileUrl)
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to retrieve Damages PDF.',
    })
    newTab?.close()
  } finally {
    disableDownloadBtn.value = false
  }
}
</script>

<template>
  <el-dialog
    v-model="open"
    width="800"
    class="dialog"
    :show-close="false"
    destroy-on-close
    data-testid="damages-preview-dialog"
  >
    <template #header>
      <div class="dialog__header">Damages / Container {{ `# ${containerNumber}` }}</div>
    </template>
    <div class="dialog__content">
      <div v-for="damageInstance in props.damages" :key="damageInstance.id" class="dialog__content__item">
        <div class="dialog__content__item__img-container">
          <img :src="damageInstance.file_url" :alt="damageInstance.description" class="dialog__content__item__img" />
        </div>
        <div class="dialog__content__item__text">{{ damageInstance.description }}</div>
      </div>
    </div>
    <template #footer>
      <div class="dialog__footer">
        <div class="dialog__footer__actions">
          <el-button :disabled="disableDownloadBtn" @click="openDamagesPDF">Export PDF</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.dialog {
  &__header {
    font-size: 20px;
    line-height: 32px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    padding: 0 10px;
    overflow-y: auto;
    max-height: 700px;

    &__item {
      display: flex;
      flex-direction: row;
      margin-bottom: 30px;

      &:last-of-type {
        margin-bottom: 0;
      }

      &__img {
        border-radius: 5px;
        max-height: 370px;
        max-width: 500px;

        &-container {
          margin-right: 25px;
          flex: 2;
          min-width: 500px;
        }
      }

      &__text {
        font-size: 14px;
        line-height: 20px;
        flex: 1;
      }
    }
  }

  &__footer {
    padding: 10px;
  }
}
</style>
